import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect as routeRedirect } from "@tanstack/react-router";
import { forgetClosingSeen, SIGN_IN_COPY, SignInLayout } from "@vd/ui";
import { useEffect, useState } from "react";
import { auth, qk } from "../api/admin";
import { setCsrfToken } from "../api/client";
import { signupStatusQuery } from "../api/signup";
import { safeReturnTo } from "../auth/device";
import { classifySignInError } from "../auth/signInErrors";
import { canonicalSignInSearch, signInSearch } from "../auth/signInSearch";
import { SignInCard } from "../components/signin/SignInCard";
import { Button, Field, Input } from "../components/ui";
import { DEV_SIGNIN_ENABLED } from "../config";
import { isAppPath } from "../lib/modeSwitch";
import { Link, useNavigate } from "../lib/navigate";

export const Route = createFileRoute("/signin")({
  staticData: { crumb: { label: "Sign in", parents: [] } },
  validateSearch: (s) => signInSearch.parse(s),
  beforeLoad: ({ location, search }) => {
    const canonical = canonicalSignInSearch(location.href, search);
    if (canonical.replace)
      throw routeRedirect({ to: "/signin", search: canonical.search, hash: "", replace: true });
  },
  component: SignIn,
});

function SignIn() {
  const { redirect, error, email, handoff, port, state } = Route.useSearch();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [devError, setDevError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [devEmail, setDevEmail] = useState("apino@vertec.io");
  const desktop = handoff && port && state ? { challenge: handoff, port, state } : undefined;
  // The next sign-in shows the closing screen again, if the account is closing (ADR 0062).
  useEffect(() => forgetClosingSeen(), []);

  const dev = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setDevError(null);
    try {
      const session = await auth.dev(devEmail.trim());
      setCsrfToken(session.csrf_token);
      qc.setQueryData(qk.me, session.me);
      const to = safeReturnTo(redirect);
      // App mode is another app on this origin: load it.
      if (isAppPath(to)) window.location.assign(to);
      else await navigate({ to });
    } catch (err) {
      setDevError(SIGN_IN_COPY[classifySignInError(err)] || null);
    } finally {
      // Still here after navigating means a guard sent us back (a Rep has no console).
      setBusy(false);
    }
  };

  return (
    <SignInLayout
      footer={
        DEV_SIGNIN_ENABLED && !desktop ? (
          <form onSubmit={dev}>
            <Field label="Development sign-in" help="Only in development builds.">
              <Input
                id="dev-email"
                type="email"
                autoComplete="off"
                value={devEmail}
                onChange={(e) => setDevEmail(e.target.value)}
                required
              />
            </Field>
            {devError ? <p className="mt-2 text-caption text-danger-ink">{devError}</p> : null}
            <Button type="submit" className="mt-3 w-full" busy={busy} disabled={busy}>
              Sign in
            </Button>
          </form>
        ) : undefined
      }
    >
      <SignInCard
        redirect={redirect}
        error={error}
        email={email}
        handoff={desktop}
        title={redirect?.startsWith("/listen/") ? "Sign in to listen to this call" : "Sign in"}
        emailFooter={desktop ? null : <CreateAccountLine />}
      />
    </SignInLayout>
  );
}

/** "New to Kite? Create an account" when the server has signup on (ADR 0052). Its line keeps
 * its height while the check loads, so nothing moves. */
function CreateAccountLine() {
  const on = useQuery(signupStatusQuery).data;
  return (
    <p className="h-4 text-center text-caption text-ink-3">
      {on ? (
        <>
          New to Kite?{" "}
          <Link to="/signup" className="font-medium text-accent-ink">
            Create an account
          </Link>
        </>
      ) : null}
    </p>
  );
}
